// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
// @todo: Migrate to 'TextField' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {TextField} from '@wandb/weave/components/Form/TextField';
import React, {useCallback} from 'react';
import {twMerge} from 'tailwind-merge';

import {
  PanelQuickAddEvent,
  trackPanelQuickAdd,
} from '../../../services/analytics/panelQuickAddEvents';
import {useWorkspaceRefsContext} from '../../Workspace/WorkspaceRefsContext';
import {useWorkspaceSettingsContext} from '../Settings/WorkspaceSettingsContextProvider';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawerContextProvider';
import {AddBulkButton} from './AddBulkButton';
import {FoundPanelsPill} from './FoundPanelsPill';
import {usePaginatedQuickAdd} from './hooks/usePaginatedQuickAdd';
import {KeyTypeIcon} from './KeyTypeIcon';
import {usePanelsQuickAddContext} from './PanelsQuickAddContext';
import {EmptyPlaceholder} from './virtuosoComponents';

export const PanelsQuickAddPaginated = () => {
  const {closeQuickAdd, initialSearchStr, onBack} = usePanelsQuickAddContext();
  const {panelBankConfigRef} = useWorkspaceRefsContext();
  const {targetSectionRef} = useWorkspaceDrawerContext();
  const isGlobalQuickAdd = !targetSectionRef;

  const {
    workspaceLayout: {sortAlphabetically},
  } = useWorkspaceSettingsContext();

  const trackQuickAddBound = useCallback(
    (event: Omit<PanelQuickAddEvent, 'scope'>) => {
      trackPanelQuickAdd({
        scope: isGlobalQuickAdd ? 'global' : 'section',
        isSearchActive: !!initialSearchStr,
        ...event,
      });
    },
    [isGlobalQuickAdd, initialSearchStr]
  );

  const {
    searchStr,
    dSearchStr,
    setSearchStr,
    regexError,
    searchedKeys,
    numPanelsByKey,
    displayedKeys,
    displayedKeysWithNoPanels,
    loadMore,
    onAddOne,
    onBulkAdd,
    cursor,
  } = usePaginatedQuickAdd({
    initialSearchStr,
    isGlobalQuickAdd,
    targetSectionRef,
    panelBankConfigRef,
    sortAlphabetically: !!sortAlphabetically,
    trackQuickAddBound,
  });

  return (
    <>
      <div className="border-b border-solid border-moon-250">
        {onBack != null && (
          <div className="flex items-center justify-between px-24 py-16">
            <Button variant="ghost" icon="back" onClick={onBack} />
            <Button variant="ghost" icon="close" onClick={closeQuickAdd} />
          </div>
        )}
        <div
          className={twMerge(
            'flex items-center justify-between px-24',
            onBack == null && 'py-16'
          )}>
          <h2 className="text-xl font-semibold leading-[32px]">
            Add panels from keys
          </h2>
          {onBack == null && (
            <Button variant="ghost" icon="close" onClick={closeQuickAdd} />
          )}
        </div>
        <p className="px-24 pb-16 text-moon-500">
          Easily generate panels from available keys. Don't see a specific key?
          Ensure that related runs are visualized in the workspace.
        </p>
        <div className="px-24 pb-16">
          <div>
            <TextField
              icon="search"
              placeholder="Search keys with regex"
              value={searchStr}
              onChange={value => setSearchStr(value)}
              onBlur={() => trackQuickAddBound({action: 'Search box blurred'})}
              errorState={!!regexError}
            />
            {regexError && (
              <p className="mt-2 text-xs text-red-500">{regexError}</p>
            )}
          </div>
        </div>
      </div>
      <div className="flex h-[48px] items-center justify-between gap-16 border-b border-solid border-moon-250 bg-moon-50 px-24">
        <div className="flex items-center gap-8">
          <span className="text-sm font-semibold text-moon-500">
            {dSearchStr ? 'RESULTS' : 'KEYS'}
          </span>
          <div className="rounded bg-oblivion/[0.03] px-4 text-xs font-semibold text-moon-500">
            {displayedKeys.length.toLocaleString()}
            {searchedKeys.hasNextPage && '+'}
          </div>
        </div>
        <AddBulkButton
          searchedKeys={displayedKeys}
          keysWithNoPanels={displayedKeysWithNoPanels}
          trackQuickAddBound={trackQuickAddBound}
          onBulkAdd={onBulkAdd}
        />
      </div>
      <div className="flex-1 overflow-auto">
        <div className="py-12">
          {cursor === null && searchedKeys.loading && !regexError && (
            <div className="flex items-center justify-center py-32">
              <div className="text-moon-500">Searching keys...</div>
            </div>
          )}

          {displayedKeys.length === 0 &&
            !searchedKeys.loading &&
            !regexError && <EmptyPlaceholder />}
          {displayedKeys.map(([key, spec]) => {
            const numPanelsGlobal = numPanelsByKey.global.get(key) ?? 0;
            const numPanelsInScope = numPanelsByKey.inScope.get(key) ?? 0;
            return (
              <div
                key={key}
                className={twMerge(
                  'flex items-center justify-between gap-16 px-24 hover:bg-moon-100',
                  isGlobalQuickAdd ? 'py-6' : 'py-10',
                  '[&:hover_button[data-test="add-one-btn"]]:visible [&_button[data-test="add-one-btn"]]:invisible'
                )}>
                <div className="flex gap-8">
                  <KeyTypeIcon
                    keyType={spec.keyType}
                    isApiAddedPanel={spec.type === 'api-added-panel'}
                  />
                  <div>
                    <p className="break-all">
                      {isGlobalQuickAdd &&
                      key.startsWith(spec.defaultSection + '/')
                        ? key.slice(spec.defaultSection.length + 1)
                        : key}
                    </p>
                    {isGlobalQuickAdd && (
                      <p className="break-all text-sm text-moon-500">
                        {spec.defaultSection}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-8">
                  {!numPanelsGlobal && (
                    <Button
                      data-test="add-one-btn"
                      variant="secondary"
                      icon="add-new"
                      size="small"
                      onClick={() => onAddOne(key, spec)}>
                      Add
                    </Button>
                  )}
                  {numPanelsGlobal > 0 && (
                    <FoundPanelsPill isInScope={numPanelsInScope > 0} />
                  )}
                </div>
              </div>
            );
          })}
          {cursor !== null && searchedKeys.loading && (
            <div className="flex items-center justify-center py-16">
              <div className="text-moon-500">Loading...</div>
            </div>
          )}
          {searchedKeys.hasNextPage && !searchedKeys.loading && (
            <div className="flex items-center justify-center py-16">
              <Button
                variant="secondary"
                onClick={loadMore}
                disabled={searchedKeys.loading}>
                Load more
              </Button>
            </div>
          )}
          {searchedKeys.error &&
            !searchedKeys.error.message
              ?.toLowerCase()
              .includes('invalid regex') && (
              <div className="flex items-center justify-center py-16">
                <div className="text-red-500">
                  Error loading keys: {searchedKeys.error.message}
                </div>
              </div>
            )}
        </div>
      </div>
    </>
  );
};
